// Voxel grid + greedy mesher.
//
// The whole ship lives in one dense Uint8 grid (1 cell = 1 m³). The mesher
// walks every axis slice and greedily merges coplanar faces of the same
// (type, shade) into quads, so the 270 m hull renders as a single static
// BufferGeometry with vertex colors — no textures, no per-block draw calls.

import * as THREE from "three";
import { V, VOX, FACE_SHADE, INTERIOR_DIM, INTERIOR_TINT } from "./const.js";

export class VoxelGrid {
  constructor(nx, ny, nz) {
    this.nx = nx; this.ny = ny; this.nz = nz;
    this.data = new Uint8Array(nx * ny * nz);
    this.topSolid = null; // per-column highest solid Y, for skylight dimming
  }
  idx(x, y, z) { return (x * this.ny + y) * this.nz + z; }
  inBounds(x, y, z) {
    return x >= 0 && y >= 0 && z >= 0 && x < this.nx && y < this.ny && z < this.nz;
  }
  get(x, y, z) {
    if (!this.inBounds(x, y, z)) return V.AIR;
    return this.data[this.idx(x, y, z)];
  }
  set(x, y, z, v) {
    if (this.inBounds(x, y, z)) this.data[this.idx(x, y, z)] = v;
  }
  solid(x, y, z) { return VOX[this.get(x, y, z)].solid === true; }
  climbable(x, y, z) { return VOX[this.get(x, y, z)].climb === true; }

  // Axis-aligned box fill, inclusive coordinates.
  fill(x0, x1, y0, y1, z0, z1, v) {
    for (let x = Math.max(0, x0); x <= Math.min(this.nx - 1, x1); x++)
      for (let y = Math.max(0, y0); y <= Math.min(this.ny - 1, y1); y++)
        for (let z = Math.max(0, z0); z <= Math.min(this.nz - 1, z1); z++)
          this.data[this.idx(x, y, z)] = v;
  }
  // Fill only where a cell is currently solid (used to carve rooms without
  // eating through into the sea).
  carve(x0, x1, y0, y1, z0, z1) {
    for (let x = Math.max(0, x0); x <= Math.min(this.nx - 1, x1); x++)
      for (let y = Math.max(0, y0); y <= Math.min(this.ny - 1, y1); y++)
        for (let z = Math.max(0, z0); z <= Math.min(this.nz - 1, z1); z++) {
          const i = this.idx(x, y, z);
          if (VOX[this.data[i]].solid) this.data[i] = V.AIR;
        }
  }

  computeSkylight() {
    this.topSolid = new Int16Array(this.nx * this.nz).fill(-1);
    for (let x = 0; x < this.nx; x++)
      for (let z = 0; z < this.nz; z++) {
        for (let y = this.ny - 1; y >= 0; y--) {
          const t = this.data[this.idx(x, y, z)];
          if (VOX[t].solid || VOX[t].glass) { this.topSolid[x * this.nz + z] = y; break; }
        }
      }
  }
  // Brightness of the *air* cell a face is exposed to.
  light(x, y, z) {
    if (!this.inBounds(x, y, z)) return 1;
    const top = this.topSolid[x * this.nz + z];
    return y > top ? 1 : INTERIOR_DIM;
  }
  // Is this point under solid cover (deck plates somewhere overhead)? The
  // sea culling asks this: an eye below the waterline with a deck over it
  // has no honest sightline to open water.
  underCover(x, y, z) {
    if (!this.topSolid) return false;
    const xi = Math.floor(x), zi = Math.floor(z);
    if (xi < 0 || zi < 0 || xi >= this.nx || zi >= this.nz) return false;
    return this.topSolid[xi * this.nz + zi] > y;
  }
}

// Greedy mesh one grid into { solidGeom, interiorGeom, glassGeom }. Exterior
// (skylit) and interior (lamp-lit) faces build separate geometries so night
// can douse the weather decks without dousing the lamps below: darken ship
// is a topside order, and the compartments keep their own light.
export function meshGrid(grid) {
  const solid = new GeomBuilder();
  const interior = new GeomBuilder();
  const glass = new GeomBuilder();
  grid.computeSkylight();

  // For each of the 3 axes, sweep slices along the axis and greedy-merge a
  // 2D mask of (type<<2 | shadeBucket) per slice, once for each face sign.
  const dims = [grid.nx, grid.ny, grid.nz];
  const mask = new Int32Array(Math.max(dims[0] * dims[1], dims[1] * dims[2], dims[0] * dims[2]));

  for (let a = 0; a < 3; a++) {
    const u = (a + 1) % 3, w = (a + 2) % 3;
    const x = [0, 0, 0], q = [0, 0, 0];
    q[a] = 1;
    const shadeKeys = a === 0 ? ["px", "nx"] : a === 1 ? ["py", "ny"] : ["pz", "nz"];

    for (x[a] = -1; x[a] < dims[a];) {
      // Build the mask for the boundary between slice x[a] and x[a]+1.
      let n = 0;
      for (x[w] = 0; x[w] < dims[w]; x[w]++)
        for (x[u] = 0; x[u] < dims[u]; x[u]++, n++) {
          const t1 = grid.get(x[0], x[1], x[2]);
          const t2 = grid.get(x[0] + q[0], x[1] + q[1], x[2] + q[2]);
          const s1 = VOX[t1].solid || VOX[t1].glass, s2 = VOX[t2].solid || VOX[t2].glass;
          let v = 0;
          if (s1 && !s2) {
            // face pointing +a, lit by the air cell beyond it
            const lit = grid.light(x[0] + q[0], x[1] + q[1], x[2] + q[2]);
            v = encode(t1, shadeKeys[0], lit, +1);
          } else if (!s1 && s2) {
            const lit = grid.light(x[0], x[1], x[2]);
            v = encode(t2, shadeKeys[1], lit, -1);
          }
          mask[n] = v;
        }
      x[a]++;

      // Greedy merge the mask.
      n = 0;
      for (let j = 0; j < dims[w]; j++) {
        for (let i = 0; i < dims[u];) {
          const c = mask[n];
          if (c === 0) { i++; n++; continue; }
          let wRun = 1;
          while (i + wRun < dims[u] && mask[n + wRun] === c) wRun++;
          let hRun = 1;
          outer: while (j + hRun < dims[w]) {
            for (let k = 0; k < wRun; k++)
              if (mask[n + k + hRun * dims[u]] !== c) break outer;
            hRun++;
          }
          // Emit quad.
          const du = [0, 0, 0], dw = [0, 0, 0];
          du[u] = wRun; dw[w] = hRun;
          const p = [0, 0, 0];
          p[a] = x[a]; p[u] = i; p[w] = j;
          const sgn = decodeSign(c);
          emitQuad(decode(c), p, du, dw, a, sgn > 0, sgn, solid, interior, glass);
          // Zero the used area.
          for (let hh = 0; hh < hRun; hh++)
            for (let k = 0; k < wRun; k++) mask[n + k + hh * dims[u]] = 0;
          i += wRun; n += wRun;
        }
      }
    }
  }

  return { solidGeom: solid.build(), interiorGeom: interior.build(), glassGeom: glass.build() };
}

// Pack (type, shadeKey, light, sign) into a nonzero int for mask comparison.
const SHADE_IDX = { px: 1, nx: 2, py: 3, ny: 4, pz: 5, nz: 6 };
const SHADE_LUT = [null, "px", "nx", "py", "ny", "pz", "nz"];
function encode(type, shadeKey, light, sign) {
  const lb = light >= 1 ? 1 : 0;
  return ((type << 6) | (SHADE_IDX[shadeKey] << 2) | (lb << 1) | (sign > 0 ? 1 : 0)) + (1 << 20);
}
function decode(c) {
  c -= 1 << 20;
  return { type: c >> 6, shade: SHADE_LUT[(c >> 2) & 15], bright: ((c >> 1) & 1) === 1 };
}
function decodeSign(c) { return ((c - (1 << 20)) & 1) ? 1 : -1; }

function emitQuad(info, p, du, dw, axis, ccw, sign, solid, interior, glass) {
  const def = VOX[info.type];
  const b = def.glass ? glass : info.bright ? solid : interior;
  const base = new THREE.Color(def.color);
  let mul = FACE_SHADE[info.shade] * (info.bright ? 1 : INTERIOR_DIM);
  let tint = info.bright ? [1, 1, 1] : INTERIOR_TINT;
  const r = base.r * mul * tint[0], g = base.g * mul * tint[1], bl = base.b * mul * tint[2];

  const v0 = p;
  const v1 = [p[0] + du[0], p[1] + du[1], p[2] + du[2]];
  const v2 = [p[0] + du[0] + dw[0], p[1] + du[1] + dw[1], p[2] + du[2] + dw[2]];
  const v3 = [p[0] + dw[0], p[1] + dw[1], p[2] + dw[2]];
  const n = [0, 0, 0];
  n[axis] = sign;
  b.quad(v0, v1, v2, v3, n, [r, g, bl], ccw);
}

class GeomBuilder {
  constructor() { this.pos = []; this.col = []; this.nrm = []; this.idx = []; this.vn = 0; }
  quad(a, b, c, d, n, col, ccw) {
    const vs = ccw ? [a, b, c, d] : [a, d, c, b];
    for (const v of vs) {
      this.pos.push(v[0], v[1], v[2]);
      this.nrm.push(n[0], n[1], n[2]);
      this.col.push(col[0], col[1], col[2]);
    }
    const i = this.vn;
    this.idx.push(i, i + 1, i + 2, i, i + 2, i + 3);
    this.vn += 4;
  }
  build() {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.col, 3));
    g.setIndex(this.idx);
    return g;
  }
}

// Materials for meshed grids. Lighting is fully baked into the vertex
// colors (face shading + interior dimming), so the mesh uses unlit
// materials — predictable, bright, deliberately blocky.
export function solidMaterial() {
  return new THREE.MeshBasicMaterial({ vertexColors: true });
}
export function glassMaterial() {
  return new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.45, depthWrite: false });
}

// Convenience: mesh a grid into a Group of solid+interior+glass meshes.
// "vox-solid" is the skylit exterior (setNight tints it); "vox-interior" is
// the lamp-lit inside faces (setNight leaves it alone).
export function gridToMeshes(grid) {
  const { solidGeom, interiorGeom, glassGeom } = meshGrid(grid);
  const group = new THREE.Group();
  const solid = new THREE.Mesh(solidGeom, solidMaterial());
  solid.name = "vox-solid";
  group.add(solid);
  if (interiorGeom.getAttribute("position") && interiorGeom.getAttribute("position").count > 0) {
    const interior = new THREE.Mesh(interiorGeom, solidMaterial());
    interior.name = "vox-interior";
    group.add(interior);
  }
  if (glassGeom.getAttribute("position") && glassGeom.getAttribute("position").count > 0) {
    const glass = new THREE.Mesh(glassGeom, glassMaterial());
    glass.name = "vox-glass";
    group.add(glass);
  }
  return group;
}
